import { type FC, useState } from 'react';
import {
  getLabelColor,
  useAppointmentLabelsEnabled,
  useGetLabelGroupsQuery,
  useGetLabelsQuery,
} from '@features/appointment-labels';
import { useOrganizationId } from '@features/user';
import CheckIcon from '@mui/icons-material/Check';
import LabelIcon from '@mui/icons-material/Label';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import {
  Box,
  Checkbox,
  Divider,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';

import { useCalendarGroupBy } from '../store/hooks';

/**
 * A label group's identity glyph: a small cluster of its member labels' colors
 * (up to 3 overlapping dots) — since a group itself has no single color.
 */
const GroupColorCluster: FC<{ colors: (string | null | undefined)[] }> = ({ colors }) => (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>
    {colors.slice(0, 3).map((color, index) => (
      <Box
        key={index}
        sx={{
          width: 11,
          height: 11,
          borderRadius: '50%',
          bgcolor: getLabelColor(color),
          border: '1.5px solid',
          borderColor: 'background.paper',
          ml: index === 0 ? 0 : '-5px',
        }}
      />
    ))}
  </Box>
);

/**
 * Day-view "Group by label" lens (GDN-2018). An icon-only trigger (outline tag
 * when off, filled + tinted when a lens is active) opens a single hierarchical
 * menu: each label GROUP is a row (lens = a lane per label) that expands to its
 * member labels (lens = one lane for that label); standalone labels sit at the
 * top level. Selecting the active row again turns the lens off — there is no
 * separate "Off" entry. The "Split lanes by provider" toggle expands each lane
 * into provider sub-columns. Renders nothing until the practice has labels.
 */
const GroupBySelector: FC = () => {
  const organizationId = useOrganizationId();
  const labelsEnabled = useAppointmentLabelsEnabled();
  const { appointmentLabels: tracking } = useEventTracking();
  const { data: groupsData } = useGetLabelGroupsQuery(
    { orgId: organizationId ?? '' },
    { skip: !organizationId || !labelsEnabled },
  );
  const { data: labelsData } = useGetLabelsQuery(
    { orgId: organizationId ?? '' },
    { skip: !organizationId || !labelsEnabled },
  );
  const {
    groupByMode,
    groupByLabelGroupId,
    splitLanesByProvider,
    setGroupBy,
    setSplitLanesByProvider,
  } = useCalendarGroupBy();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const groups = groupsData?.items ?? [];
  const labels = labelsData?.items ?? [];
  const standaloneLabels = labels.filter(label => !label.label_group_id);

  if (!labelsEnabled || (groups.length === 0 && labels.length === 0)) return null;

  const isActive = groupByMode !== 'off';
  const currentName =
    groupByMode === 'group'
      ? (groups.find(g => g.label_group_id === groupByLabelGroupId)?.name ?? 'group')
      : groupByMode === 'label'
        ? (labels.find(l => l.label_id === groupByLabelGroupId)?.name ?? 'label')
        : '';

  // Selecting the active row again clears the lens (no separate "Off" entry).
  const toggleGroupLens = (groupId: string) => {
    const active = groupByMode === 'group' && groupByLabelGroupId === groupId;
    setGroupBy(active ? 'off' : 'group', active ? null : groupId);
    tracking.calendarLensSelected({ lens_type: active ? 'off' : 'group' });
  };
  const toggleLabelLens = (labelId: string) => {
    const active = groupByMode === 'label' && groupByLabelGroupId === labelId;
    setGroupBy(active ? 'off' : 'label', active ? null : labelId);
    tracking.calendarLensSelected({ lens_type: active ? 'off' : 'label' });
  };

  const labelRow = (label: (typeof labels)[number], indented: boolean) => {
    const selected = groupByMode === 'label' && groupByLabelGroupId === label.label_id;
    return (
      <MenuItem
        key={label.label_id}
        selected={selected}
        onClick={() => toggleLabelLens(label.label_id)}
        sx={{ gap: 1, pl: indented ? 4.5 : 2 }}
      >
        <LabelIcon fontSize="small" sx={{ color: getLabelColor(label.color), flexShrink: 0 }} />
        <ListItemText primary={label.name} />
        {selected && <CheckIcon fontSize="small" sx={{ color: 'primary.main' }} />}
      </MenuItem>
    );
  };

  const groupItems = groups.flatMap(group => {
    const groupLabels = labels.filter(label => label.label_group_id === group.label_group_id);
    if (groupLabels.length === 0) return [];
    const selected = groupByMode === 'group' && groupByLabelGroupId === group.label_group_id;
    return [
      <MenuItem
        key={group.label_group_id}
        selected={selected}
        onClick={() => toggleGroupLens(group.label_group_id)}
        sx={{ gap: 1, pl: 2 }}
      >
        <GroupColorCluster colors={groupLabels.map(label => label.color)} />
        <ListItemText primary={group.name} slotProps={{ primary: { sx: { fontWeight: 600 } } }} />
        {selected && <CheckIcon fontSize="small" sx={{ color: 'primary.main' }} />}
      </MenuItem>,
      ...groupLabels.map(label => labelRow(label, true)),
    ];
  });

  return (
    <>
      <Tooltip title={isActive ? `Showing: ${currentName}` : 'Group by label'}>
        <IconButton
          aria-label="Group by label"
          onClick={event => setAnchorEl(event.currentTarget)}
          sx={{
            border: '1px solid',
            borderRadius: 1.5,
            p: 0.75,
            borderColor: isActive ? 'primary.main' : 'rgba(0, 13, 77, 0.25)',
            color: isActive ? 'primary.main' : '#535976',
            bgcolor: isActive ? 'rgba(54, 34, 158, 0.10)' : 'transparent',
            '&:hover': {
              bgcolor: isActive ? 'rgba(54, 34, 158, 0.16)' : 'action.hover',
              borderColor: isActive ? 'primary.main' : '#535976',
            },
          }}
        >
          {isActive ? <LabelIcon fontSize="small" /> : <LabelOutlinedIcon fontSize="small" />}
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        sx={{ '& .MuiPaper-root': { minWidth: 240, maxHeight: 420 } }}
      >
        {groupItems}
        {standaloneLabels.map(label => labelRow(label, false))}

        <Divider />
        <MenuItem
          disabled={!isActive}
          onClick={() => {
            setSplitLanesByProvider(!splitLanesByProvider);
            tracking.calendarSplitLanesToggled({ enabled: !splitLanesByProvider });
          }}
          sx={{ gap: 1, pl: 2 }}
        >
          <Checkbox
            size="small"
            checked={splitLanesByProvider}
            disabled={!isActive}
            tabIndex={-1}
            disableRipple
            sx={{ p: 0 }}
          />
          <ListItemText primary="Split lanes by provider" />
        </MenuItem>
      </Menu>
    </>
  );
};

export default GroupBySelector;
